import { Stack } from "@mui/material"
import { Button, Dropdown, Typography, getCalendarFormat, Link, fetchRoute, TPromisePoolPartialList, TPromisePoolReturnData } from "@cakemail-org/ui-components-v2"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { NameTableCol } from "../../../components/tables/nameTableCol"
import { ERoutes } from "../../../utils/url"
import { getABTestStatus, calculateClickRate, calculateUnsubscribeRate, calculateBounceRate } from "./index"
import { TABTestWithStats, TABTestStats, EABTestsNav } from "../types"

export type TABTestRowBuilderProps = {
    abTest: TABTestWithStats
    tabFilter: EABTestsNav
    partialLists: TPromisePoolReturnData<TPromisePoolPartialList>
    thumbnails: Record<number, { pathA?: string; pathB?: string; pathC?: string; pathD?: string }>
    getActionsDropdownOptions: (abTest: TABTestWithStats) => TDropMenuItem[]
    getThumbnailUrl: (abTest: TABTestWithStats) => string | undefined
    handleViewStats: (abTest: TABTestWithStats) => void
    handleEditABTest: (abTest: TABTestWithStats) => void
    t: (key: string, options?: any) => string
}

/**
 * Builds a table row object for an A/B test
 * This is a utility function that returns a plain object, not a React component
 *
 * @param props - Configuration for building the row
 * @returns Table row object with id and content properties for each column
 */
export function buildABTestRow({
    abTest,
    tabFilter,
    partialLists,
    getThumbnailUrl,
    getActionsDropdownOptions,
    handleViewStats,
    handleEditABTest,
    t
}: TABTestRowBuilderProps) {

    const status = getABTestStatus(abTest)
    const listName = partialLists?.[abTest.list_id]?.name || ""

    const actions = (
        <Stack direction="row" gap={3}>
            <Button
                variant="outlined"
                size="small"
                color="body1"
                onClick={() => {
                    if (status === "completed") {
                        handleViewStats(abTest)
                    } else {
                        handleEditABTest(abTest)
                    }
                }}
            >
                {t(status === "completed" ? "general-view-report" : "general-open_one")}
            </Button>
            <Dropdown
                size="medium"
                color="body1"
                variant="outlined"
                iconOnly
                options={getActionsDropdownOptions(abTest)}
            />
        </Stack>
    )

    const baseRow = {
        name: {
            id: abTest.name,
            content: <NameTableCol
                name={abTest.name}
                imgSrc={getThumbnailUrl(abTest)}
            />
        },
        list_id: {
            id: abTest.list_id?.toString() || "",
            content: <Link href={"#" + fetchRoute({ route: ERoutes.contacts }) + "?listId=" + (abTest?.list_id || "")}>
                {listName || abTest.list_id || ""}
            </Link>
        }
    }

    if (tabFilter === EABTestsNav.Active) {
        const clickRateA = calculateClickRate(abTest.statsA)
        const unsubscribeRateA = calculateUnsubscribeRate(abTest.statsA)
        const bounceRateA = calculateBounceRate(abTest.statsA)

        const clickRateB = calculateClickRate(abTest.statsB)
        const unsubscribeRateB = calculateUnsubscribeRate(abTest.statsB)
        const bounceRateB = calculateBounceRate(abTest.statsB)

        return {
            ...baseRow,
            pathA_click_rate: {
                id: clickRateA.toString(),
                content: <Typography variant="body1L">
                    {abTest.statsA ? `${clickRateA.toFixed(1)}%` : "-"}
                </Typography>
            },
            pathA_unsubscribe_rate: {
                id: unsubscribeRateA.toString(),
                content: <Typography variant="body1L">
                    {abTest.statsA ? `${unsubscribeRateA.toFixed(1)}%` : "-"}
                </Typography>
            },
            pathA_bounce_rate: {
                id: bounceRateA.toString(),
                content: <Typography variant="body1L">
                    {abTest.statsA ? `${bounceRateA.toFixed(1)}%` : "-"}
                </Typography>
            },
            pathB_click_rate: {
                id: clickRateB.toString(),
                content: <Typography variant="body1L">
                    {abTest.statsB ? `${clickRateB.toFixed(1)}%` : "-"}
                </Typography>
            },
            pathB_unsubscribe_rate: {
                id: unsubscribeRateB.toString(),
                content: <Typography variant="body1L">
                    {abTest.statsB ? `${unsubscribeRateB.toFixed(1)}%` : "-"}
                </Typography>
            },
            pathB_bounce_rate: {
                id: bounceRateB.toString(),
                content: <Typography variant="body1L">
                    {abTest.statsB ? `${bounceRateB.toFixed(1)}%` : "-"}
                </Typography>
            },
            actions: {
                id: "actions",
                content: actions
            }
        }
    }

    if (tabFilter === EABTestsNav.Completed) {
        const hasWinnerC = abTest.statsC?.data?.groupedByContact?.sent && abTest.statsC.data.groupedByContact.sent > 0
        const hasWinnerD = abTest.statsD?.data?.groupedByContact?.sent && abTest.statsD.data.groupedByContact.sent > 0

        let winnerStats: TABTestStats | undefined = undefined
        if (hasWinnerC) {
            winnerStats = abTest.statsC
        } else if (hasWinnerD) {
            winnerStats = abTest.statsD
        }

        const clickRate = calculateClickRate(winnerStats)
        const unsubscribeRate = calculateUnsubscribeRate(winnerStats)
        const bounceRate = calculateBounceRate(winnerStats)

        return {
            ...baseRow,
            winner_click_rate: {
                id: clickRate.toString(),
                content: <Typography variant="body1L">
                    {winnerStats ? `${clickRate.toFixed(1)}%` : ""}
                </Typography>
            },
            winner_unsubscribe_rate: {
                id: unsubscribeRate.toString(),
                content: <Typography variant="body1L">
                    {winnerStats ? `${unsubscribeRate.toFixed(1)}%` : ""}
                </Typography>
            },
            winner_bounce_rate: {
                id: bounceRate.toString(),
                content: <Typography variant="body1L">
                    {winnerStats ? `${bounceRate.toFixed(1)}%` : ""}
                </Typography>
            },
            actions: {
                id: "actions",
                content: actions
            }
        }
    }

    return {
        ...baseRow,
        created_on: {
            id: abTest.created_on,
            content: <Typography variant="body1L">
                {abTest.created_on !== 0 ? getCalendarFormat({ unixDate: abTest.created_on, showTime: true }) : ""}
            </Typography>
        },
        updated_on: {
            id: abTest.updated_on,
            content: <Typography variant="body1L">
                {abTest.updated_on !== 0 ? getCalendarFormat({ unixDate: abTest.updated_on, showTime: true }) : ""}
            </Typography>
        },
        actions: {
            id: "actions",
            content: actions
        }
    }
}
